import "@fontsource/unbounded";
import { Box, CircularProgress } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-license-pro";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Cookies from "js-cookie";
import { usePostHog } from "posthog-js/react";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import AdminPage from "./Admin/AdminPage";
import EvalResults from "./Admin/EvalResults";
import MarketingTools from "./CustomMessages/MarketingTools";
import DataLabelingDashboard from "./DataLabelingDashboard/DataLabelingDashboard";
import DataLabelingPrivateRoute from "./DataLabelingPrivateRoute";
import PageNotFound from "./ErrorHandling";
import "./global.css";
import ExpandedTextActivity from "./Irl/ExpandedTextActivity";
import Layout from "./Layout";
import AccountDetails from "./Login/AccountDetails";
import SignUp from "./Login/SignUpPhoneNumber";
import StaffLogin from "./Login/StaffLogin";
import PrivateRoute from "./PrivateRoute";
import SharePage from "./Share/SharePage";
import { checkAuthenticated } from "./user/userSlice";

const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1875D1",
      contrastText: "#fff",
    },
    background: {
      default: "#000000",
      paper: "#000000",
    },
  },
  typography: {
    fontFamily: '"Unbounded", "Arial", sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontWeight: 700,
    },
    body1: {
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none", // This will prevent text from being all uppercase
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none", // Prevents text from being all uppercase
          "&.Mui-disabled": {
            border: "none", // Removes blue border on disabled buttons
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            border: "none", // Removes blue border on disabled icon buttons
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          //backgroundColor: '#e0e0e0',
          color: "black",
          fontWeight: "bold",
          height: "32px",
          padding: "4px 13.5px",
          marginRight: "8px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "transparent", // Disables highlighting on hover
            color: "#112967",
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#000000",
          "&::before": {
            content: '""',
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#000000",
            zIndex: -1,
          },
        },
        "#root": {
          backgroundColor: "#000000",
          minHeight: "100vh",
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: "#FF3B30",
        },
      },
    },
  },
});

LicenseInfo.setLicenseKey(
  "ca8f88d97a7ff75606a558b3ed69ae2fTz05MTc3NCxFPTE3NDg5OTAzMDUwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI="
);

export default function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const user = useSelector((state) => state.user);

  const posthog = usePostHog();

  useEffect(() => {
    // Only check if auth state is null (initial load)
    // Don't check if we're already logged in or explicitly logged out
    if (isAuthenticated === null) {
      dispatch(checkAuthenticated());
    }
  }, []);

  const useSessionCheck = () => {
    useEffect(() => {
      const checkSessionExpiry = () => {
        const loginTimestamp = Cookies.get("loginTimestamp");
        const currentTime = new Date().getTime();
        const eightHoursInMilliseconds = 8 * 60 * 60 * 1000;

        if (!loginTimestamp) {
          Cookies.set("loginTimestamp", currentTime);
        } else if (
          currentTime - parseInt(loginTimestamp, 10) >
          eightHoursInMilliseconds
        ) {
          Cookies.remove("loginTimestamp");
          window.location.href = `${
            window.location.pathname
          }?refresh=${new Date().getTime()}`;
        }
      };

      checkSessionExpiry();
      const timer = setInterval(checkSessionExpiry, 60000); // Check every minute

      return () => clearInterval(timer);
    }, []);
  };

  useSessionCheck();

  useEffect(() => {
    const checkBundleVersion = async () => {
      try {
        const response = await fetch("/api/bundle-version/");
        const data = await response.json();

        // Add debug logging
        console.log("Current bundle version:", window.CURRENT_BUNDLE_VERSION);
        console.log("Server bundle version:", data.version);

        // Compare with version from window object (set in template)
        if (data.version !== window.CURRENT_BUNDLE_VERSION) {
          posthog.capture("bundle_version_updated", {
            current_version: window.CURRENT_BUNDLE_VERSION,
            server_version: data.version,
          });
          console.log("Version mismatch - refreshing page");
          window.location.reload(true);
        }
      } catch (error) {
        console.error("Failed to check bundle version:", error);
      }
    };

    // Check when visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkBundleVersion();
      }
    };

    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initial check
    checkBundleVersion();

    // Cleanup
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  if (isAuthenticated === null) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100vw",
          height: "100vh",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "#000000",
        }}
      >
        <CircularProgress sx={{ color: "red" }} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <GoogleOAuthProvider clientId="761277669603-726gi9uogah7ckffdq8ukn95vihg67si.apps.googleusercontent.com">
        <CssBaseline enableColorScheme />
        <Box
          sx={{
            bgcolor: "#000000",
            minHeight: "100vh",
            width: "100%",
          }}
        >
          <BrowserRouter>
            <Routes>
              <Route
                path="/shared/text-activity/:id"
                element={
                  <Layout hideNavBar>
                    <ExpandedTextActivity isSharedView={true} />
                  </Layout>
                }
              />
              <Route
                path="/share/:referralCode"
                element={
                  <Layout hideNavBar>
                    <SharePage />
                  </Layout>
                }
              />
              <Route
                path="/"
                element={
                  <Layout>
                    <PrivateRoute>
                      <MarketingTools />
                    </PrivateRoute>
                  </Layout>
                }
              />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/staff/login" element={<StaffLogin />} />
              <Route
                path="/account"
                element={
                  <Layout>
                    <PrivateRoute>
                      <AccountDetails />
                    </PrivateRoute>
                  </Layout>
                }
              />
              <Route
                path="/text-activity/:id"
                element={
                  <Layout>
                    <PrivateRoute>
                      <ExpandedTextActivity />
                    </PrivateRoute>
                  </Layout>
                }
              />
              <Route
                path="/admin"
                element={
                  <Layout>
                    <PrivateRoute>
                      <AdminPage />
                    </PrivateRoute>
                  </Layout>
                }
              />
              <Route
                path="/admin/text-activities/:id?"
                element={
                  <Layout>
                    <PrivateRoute>
                      <AdminPage />
                    </PrivateRoute>
                  </Layout>
                }
              />
              <Route
                path="/admin/eval-results"
                element={
                  <Layout>
                    <PrivateRoute>
                      <EvalResults />
                    </PrivateRoute>
                  </Layout>
                }
              />
              <Route
                path="/custom-messages"
                element={
                  <PrivateRoute>
                    <Navigate to="/" replace />
                  </PrivateRoute>
                }
              />
              <Route
                path="/data-labeling/dashboard"
                element={
                  <Layout>
                    <PrivateRoute>
                      <DataLabelingPrivateRoute>
                        <DataLabelingDashboard />
                      </DataLabelingPrivateRoute>
                    </PrivateRoute>
                  </Layout>
                }
              />
              <Route
                path="/data-labeling/dashboard/:id"
                element={
                  <Layout>
                    <PrivateRoute>
                      <DataLabelingPrivateRoute>
                        <DataLabelingDashboard />
                      </DataLabelingPrivateRoute>
                    </PrivateRoute>
                  </Layout>
                }
              />
              <Route
                path="*"
                element={
                  <Layout>
                    <PrivateRoute>
                      <PageNotFound />
                    </PrivateRoute>
                  </Layout>
                }
              />
            </Routes>
          </BrowserRouter>
        </Box>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
}
